import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import Popper from '@material-ui/core/Popper';
import Typography from '@material-ui/core/Typography';
import withWidth from '@material-ui/core/withWidth';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { Box, Button, Icon } from '@material-ui/core';
import { setDisableHintMessages } from '../Reducers/HintMessages/Actions';
import { useDispatch } from 'react-redux';
const textShadow = '0px 0px 4px rgba(255, 255, 255, 0.8)';

const useStyles = makeStyles(
  (theme) => ({
    popoverRoot: {
      [theme.breakpoints.down('xs')]: {
        display: 'none',
      },
    },
    hintTitle: {
      color: 'white',
      fontWeight: 'bold',
    },
    hintContent: {
      color: 'white',
      padding: '10px 0px',
    },
    container: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      maxWidth: 330,
      minWidth: 330,
    },
    popper: {
      zIndex: 2,
    },
    topContainer: ({ offset, perpendicularOffset }) => ({
      top: -offset,
      right: perpendicularOffset,
      flexDirection: 'column-reverse',
    }),
    leftContainer: ({ offset, perpendicularOffset }) => ({
      right: offset,
      top: perpendicularOffset,
      flexDirection: 'row-reverse',
    }),
    bottomContainer: ({ offset, perpendicularOffset }) => ({
      bottom: -offset,
      right: perpendicularOffset,
      flexDirection: 'column',
    }),
    rightContainer: ({ offset, perpendicularOffset }) => ({
      top: perpendicularOffset,
      left: offset,
      flexDirection: 'row',
    }),
    animation: {
      animation: '$left 1s infinite',
      display: 'flex',
      alignItems: 'center',
      '&:before': {
        fontSize: '1.5em',
      },
    },
    topAnimation: {
      animationName: '$top',
    },
    rightAnimation: {
      animationName: '$right',
    },
    bottomAnimation: {
      animationName: '$bottom',
    },
    leftAnimation: {
      animationName: '$left',
    },
    icon: {
      margin: '0 5px',
      textShadow,
    },
    leftIcon: {
      transform: 'rotate(0deg)',
    },
    topIcon: {
      transform: 'rotate(90deg)',
    },
    rightIcon: {
      transform: 'rotate(180deg)',
    },
    bottomIcon: {
      transform: 'rotate(270deg)',
    },
    typography: {
      textAlign: 'center',
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('xs')]: {
        fontSize: '1rem',
      },
    },
    hintText: {
      textShadow,
    },
    popoverBox: {
      position: 'relative',
      marginTop: '10px',
      padding: 10,
      backgroundColor: '#000000',
      width: '100%',
      '& p': {
        fontSize: 12,
      },
      '&:before': {
        content: '""',
        display: 'none',
        position: 'absolute',
        width: 12,
        height: 12,
        transform: 'rotate(45deg)',
        backgroundColor: '#000000',
      },
    },
    leftPopoverBox: {
      borderLeft: '5px solid ' + theme.palette.primary.main,
      '&:before': {
        right: '-6px',
        display: 'block',
      },
    },
    rightTopArrow: {
      '&:before': {
        top: 16,
        display: 'block',
      },
    },
    bottomLeftArrow: {
      '&:before': {
        bottom: -4,
        display: 'block',
      },
    },
    rightBottomArrow: {
      '&:before': {
        bottom: 16,
        display: 'block',
      },
    },
    bottomPopoverBox: {
      '&:before': {
        left: 'calc(50% - 6px)',
        top: -6,
      },
    },
    actions: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    discardBtn: {
      color: theme.palette.primary.main,
      textTransform: 'math-auto',
      fontSize: 12,
      paddingLeft: 0,
    },
    btn: {
      backgroundColor: theme.palette.primary.main,
      color: '#ffffff',
      fontSize: 12,
      fontWeight: 'bold',
      borderRadius: 0,
      padding: 2,
      marginLeft: 10,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        opacity: 0.5,
      },
    },
    close: {
      color: '#ffffff',
      position: 'absolute',
      fontSize: 14,
      right: 5,
      cursor: 'pointer',
    },
  }),
  { name: 'HintDialog' }
);

const HintDialog = (props) => {
  const {
    anchorRef,
    offsetDirection,
    width,
    title,
    content,
    onNextClick,
    onBackClick,
    arrowPlacement,
    hideArrow,
  } = props;
  const dispatch = useDispatch();
  const classes = useStyles(props);
  const [open, setOpen] = useState(true);

  if (!anchorRef || width === 'xs' || Services.designData.isInPreviewMode) {
    return null;
  }

  return (
    <Popper className={classes.popper} open={open} anchorEl={anchorRef} placement={offsetDirection}>
      <div className={clsx(classes.container, classes[`${offsetDirection}Container`])}>
        <Box
          className={clsx(
            classes.popoverBox,
            classes[`${offsetDirection}PopoverBox`],
            !hideArrow && classes[`${arrowPlacement}Arrow`]
          )}
        >
          <Icon className={classes.close} onClick={() => setOpen(false)}>
            close
          </Icon>
          <Typography className={classes.hintTitle} variant="body2">
            {title}
          </Typography>
          <Typography className={classes.hintContent} variant="body2">
            {content}
          </Typography>
          <div className={classes.actions}>
            <Button className={classes.discardBtn} onClick={() => dispatch(setDisableHintMessages())}>
              {t('hintMessages.discardHintMessages')}
            </Button>
            <div>
              {onBackClick && (
                <Button className={classes.btn} onClick={onBackClick}>
                  {t('hintMessages.back')}
                </Button>
              )}
              {onNextClick && (
                <Button className={classes.btn} onClick={onNextClick}>
                  {t('hintMessages.next')}
                </Button>
              )}
            </div>
          </div>
        </Box>
      </div>
    </Popper>
  );
};

HintDialog.propTypes = {
  offsetDirection: PropTypes.oneOf(['top', 'right', 'bottom', 'left']).isRequired,
  offset: PropTypes.number,
  perpendicularOffset: PropTypes.number,
  anchorRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  width: PropTypes.string,
  title: PropTypes.string,
  content: PropTypes.string,
  onNextClick: PropTypes.func,
  onBackClick: PropTypes.func,
  arrowPlacement: PropTypes.string,
  hideArrow: PropTypes.bool,
};

HintDialog.defaultProps = {
  offset: 100,
  perpendicularOffset: 0,
  arrowPlacement: 'rightTop',
  hideArrow: false,
};

export default withWidth({ initialWidth: 'lg' })(HintDialog);
